import { BoundingSphere,sphereOverlaps } from "./bounding_sphere";

export type Aabb = {
  min: Float32Array; // vec3
  max: Float32Array; // vec3
};

export function createAabb(
  minX = 0, minY = 0, minZ = 0,
  maxX = 0, maxY = 0, maxZ = 0
): Aabb {
  return {
    min: new Float32Array([minX, minY, minZ]),
    max: new Float32Array([maxX, maxY, maxZ]),
  };
}

export function cloneAabb(src: Aabb): Aabb {
  return {
    min: new Float32Array(src.min),
    max: new Float32Array(src.max),
  };
}

export function getAabbCenter(aabb: Aabb): Float32Array {
  return new Float32Array([
    (aabb.min[0] + aabb.max[0]) * 0.5,
    (aabb.min[1] + aabb.max[1]) * 0.5,
    (aabb.min[2] + aabb.max[2]) * 0.5,
  ]);
}

export function getAabbSize(aabb: Aabb): Float32Array {
  return new Float32Array([
    aabb.max[0] - aabb.min[0],
    aabb.max[1] - aabb.min[1],
    aabb.max[2] - aabb.min[2],
  ]);
}

export function getAabbExtents(aabb: Aabb): Float32Array {
  const size = getAabbSize(aabb);
  return new Float32Array([
    size[0] * 0.5,
    size[1] * 0.5,
    size[2] * 0.5,
  ]);
}

export function createEmptyAabb(): Aabb {
  return {
    min: new Float32Array([ Infinity,  Infinity,  Infinity]),
    max: new Float32Array([-Infinity, -Infinity, -Infinity]),
  };
}

export function expandAabbByPoint(aabb: Aabb, point: Float32Array): void {
  aabb.min[0] = Math.min(aabb.min[0], point[0]);
  aabb.min[1] = Math.min(aabb.min[1], point[1]);
  aabb.min[2] = Math.min(aabb.min[2], point[2]);

  aabb.max[0] = Math.max(aabb.max[0], point[0]);
  aabb.max[1] = Math.max(aabb.max[1], point[1]);
  aabb.max[2] = Math.max(aabb.max[2], point[2]);
}



export function aabbOverlaps(a: Aabb, b: Aabb): boolean {
  return (
    a.min[0] <= b.max[0] && a.max[0] >= b.min[0] &&
    a.min[1] <= b.max[1] && a.max[1] >= b.min[1] &&
    a.min[2] <= b.max[2] && a.max[2] >= b.min[2]
  );
}



export function aabbOverlapsSphere(aabb: Aabb, sphere: BoundingSphere): boolean {
  const closestX = clamp(sphere.center[0], aabb.min[0], aabb.max[0]);
  const closestY = clamp(sphere.center[1], aabb.min[1], aabb.max[1]);
  const closestZ = clamp(sphere.center[2], aabb.min[2], aabb.max[2]);

  const dx = sphere.center[0] - closestX;
  const dy = sphere.center[1] - closestY;
  const dz = sphere.center[2] - closestZ;

  const distanceSq = dx * dx + dy * dy + dz * dz;
  return distanceSq <= sphere.radius * sphere.radius;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}


export function aabbContainsPoint(
  aabb: Aabb,
  point: Float32Array
): boolean {
  return (
    point[0] >= aabb.min[0] && point[0] <= aabb.max[0] &&
    point[1] >= aabb.min[1] && point[1] <= aabb.max[1] &&
    point[2] >= aabb.min[2] && point[2] <= aabb.max[2]
  );
}

export function overlaps(
  aabb: Aabb | null,
  sphere: BoundingSphere | null,
  otherAabb: Aabb | null,
  otherSphere: BoundingSphere | null
): boolean {
  if (aabb && otherAabb) return aabbOverlaps(aabb, otherAabb);
  if (sphere && otherSphere) return sphereOverlaps(sphere, otherSphere);
  if (aabb && otherSphere) return aabbOverlapsSphere(aabb, otherSphere);
  if (sphere && otherAabb) return aabbOverlapsSphere(otherAabb, sphere);
  return false;
}

